import { CoverageBetaActivityTracker } from "@/components/coverage-beta/CoverageBetaActivityTracker";
import { CoverageBetaHeader } from "@/components/coverage-beta/coverage-beta-header/CoverageBetaHeader";
import { CoverageBetaSummaryView } from "@/components/coverage-beta/coverage-beta-right-panel/coverage-beta-summary-view/CoverageBetaSummaryView";
import { CoverageBetaRightPanel } from "@/components/coverage-beta/coverage-beta-right-panel/CoverageBetaRightPanel";
import { COVERAGE_BETA_SIDEBAR_WIDTH } from "@/components/coverage-beta/coverage-beta-sidebar/CoverageBetaSidebar";
import { QueryImageRelated } from "@/components/coverage-beta/query-image-related/QueryImageRelated";
import { CoverageBetaTraining } from "@/components/coverage-beta/coverage-beta-training/CoverageBetaTraining";
import { HEADER_HEIGHT } from "@/config/data";
import { useCoverageBetaHotkeys } from "@/hooks/useCoverageBetaHotkeys";
import { useFetchGalleryImgUrl } from "@/hooks/useFetchGalleryImgUrl";
import { usePropagatedPrefill } from "@/hooks/usePropagatedPrefill";
import { useRelatedFramesEnabled } from "@/hooks/useTaskRelatedFrames";
import { useBounceToJobs } from "@/hooks/useBounceToJobs";
import { useProfileHome } from "@/hooks/useProfileHome";
import { SampleOosPrediction } from "@/dev/sample-out-of-stock-predictions";
import { isDev } from "@/utils/utils";
import {
  IdleTimeProvider,
  IdleWarningModal,
  ExitModal,
} from "@labeling-tools/ui-components/src";
import {
  IdxToFeEmptyArea,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import type { EmptyAreaDetection } from "@/types/coverage";
import { buildCoverageBetaHydration } from "@/utils/hydrate-coverage-beta-from-result";
import { getGroupImages } from "@/utils/groupedImages";
import {
  getOrderedQaItems,
  mapCoverageBetaQaVerdicts,
} from "@/utils/build-coverage-beta-qa-result";
import type { ProductSchema } from "@labeling-tools/api/atlas";
import type { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { JobStatus, JobTaskStatus } from "@augmodo/lebron-browser-client";
import { useAtlasProducts } from "@labeling-tools/api/hooks";
import {
  useFetchJob,
  useBenchmarkQuizMetrics,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { usePostHog } from "@posthog/react";
import {
  mvpApiGetNextPendingJobTaskOptions,
  mvpApiGetOosParcelOptions,
} from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGate } from "statsig-react";
import { Button, LoadingOverlay } from "@mantine/core";
import { QuizMetricCard } from "@/components/QuizMetricCard";

const QUERY_WIDTH_PERCENT = 40;
const RIGHT_WIDTH_PERCENT = 60;
// How long to wait for the query + active reference images before warming the
// planogram up anyway (see isWarmupReady).
const WARMUP_TIMEOUT_MS = 10_000;

/**
 * PostHog session replay for CoverageBeta labeling sessions. OFF for now.
 *
 * To enable later: flip this to `true` (or wire it to an env var / Statsig gate
 * for a no-code toggle) AND enable Session Replay in the PostHog project
 * settings. Before recording all users, consider sampling and a slow-connection
 * guard — replay adds main-thread cost during pan/zoom and upload cost on slow
 * links, and the signed-URL shelf images won't render in replays older than ~1h.
 *
 * The analytics events (`cb_image_load`, `cb_task_viewed`, interaction counts)
 * are unaffected by this flag and keep recording.
 */
const ENABLE_SESSION_RECORDING = false;

interface Props {
  adminTask?: JobTaskDetailSchema;
  /** The completed task `result`, hydrated for read-only viewing in admin. */
  adminResult?: Record<string, unknown>;
  /**
   * Coverage Beta QA: this task is a `COVERAGE_REVIEW_BETA_QA` task (audit
   * review of a completed Coverage Beta submission), not a normal Coverage
   * Beta submission. Combined with `adminTask`/`adminResult` for admin
   * browsing of a QA task, or alone for a labeler taking one.
   */
  qaMode?: boolean;
  /**
   * Editable Coverage QA: a whole-review `COVERAGE_REVIEW_QA` task (lebron
   * `qa_scope: "task"`). Opens the labeler's submission with every labeling
   * tool enabled so the QA reviewer fixes it directly, then submits the
   * corrected result. Unlike `qaMode`, there are no per-box verdicts.
   */
  qaEdit?: boolean;
}

export const CoverageBetaTask = (props: Props = {}) => {
  const { adminTask, adminResult, qaMode, qaEdit } = props;
  const isAdmin = !!adminTask;
  const isQa = !!qaMode;
  const isQaEdit = !!qaEdit;
  // Where "Exit"/exhaustion navigate back to, and which dashboard tab a QA
  // labeler's empty states link to.
  let exitTab = "coverage-beta";
  if (isQa) exitTab = "coverage-beta-qa";
  else if (isQaEdit) exitTab = "coverage-qa";
  const params = useParams<{ jobId: string }>();
  const job_id = params?.jobId ?? "";
  const navigate = useNavigate();
  const { jobsPath } = useProfileHome();

  useCoverageBetaHotkeys();
  // Suspends the idle-warning timer while the onboarding tutorial is open —
  // a labeler reading a step for 60s+ shouldn't get flagged as idle. Closing
  // the tutorial restarts a fresh timeout (IdleTimeContext's effect deps
  // include `timeout`), so no heartbeat/assignment risk either way.
  const tutorialActive = useCoverageBetaStore((s) => s.tutorialActive);

  // Session replay, scoped to the CoverageBeta labeling view (viewer only) and
  // gated by ENABLE_SESSION_RECORDING (off for now — see the flag above).
  const posthog = usePostHog();
  useEffect(() => {
    if (!ENABLE_SESSION_RECORDING || isAdmin) return;
    posthog?.startSessionRecording();
    return () => {
      posthog?.stopSessionRecording();
    };
  }, [isAdmin, posthog]);

  // Data fetching

  const {
    data: nextPendingTaskResp,
    // isLoading: nextPendingTaskLoading,
    // isError: nextPendingTaskError,
    error: nextPendingTaskError,
  } = useQuery({
    ...mvpApiGetNextPendingJobTaskOptions({ path: { job_id } }),
    enabled: !!job_id && !adminTask,
  });

  const isTaskExhausted =
    typeof nextPendingTaskError === "string" &&
    nextPendingTaskError === "No pending tasks available";

  // Whether this is a quiz has to come from the *job*, not the task — once
  // the last question is answered there is no next pending task, so a
  // task-level check alone would be false exactly when the quiz just
  // finished. Mirrors OosConfirmationContent.tsx's isBenchmarkJob. String
  // literal until @augmodo/lebron-browser-client picks up the enum.
  const { data: job } = useFetchJob(job_id);
  const isBenchmarkJob = job?.type === "COVERAGE_REVIEW_BENCHMARK_TASKS";
  const { value: benchmarkEnabled } = useGate("fe_enable_oos_benchmark_quiz");

  // Scored results for a quiz the reviewer just finished. Gated with the rest
  // of the benchmark feature so a treatment-group-only quiz can't leak a
  // card, and only fetched once the quiz is actually out of questions.
  const { data: quizMetrics, isLoading: quizMetricsLoading } =
    useBenchmarkQuizMetrics(
      !isAdmin && isBenchmarkJob && benchmarkEnabled && isTaskExhausted
        ? job_id || null
        : null,
    );

  // Whether this visit was served a task at all — if so, exhaustion means the
  // labeler just finished the job rather than finding it already taken.
  const servedTaskRef = useRef(false);
  if (nextPendingTaskResp) servedTaskRef.current = true;

  const bounceToJobs = useBounceToJobs();
  useEffect(() => {
    // Benchmark jobs handle exhaustion in the render below (a finished quiz
    // shows its score, not a redirect) — never auto-navigate away for one.
    if (!isTaskExhausted || isBenchmarkJob) return;
    bounceToJobs(
      exitTab,
      job_id,
      servedTaskRef.current ? "finished" : "unavailable",
    );
  }, [isTaskExhausted, isBenchmarkJob, bounceToJobs, exitTab, job_id]);

  const taskDetailResp = adminTask || nextPendingTaskResp;
  // Benchmark quiz tasks are served only to the treatment group (both
  // dedicated quiz jobs and silently injected ones in normal jobs) —
  // everyone else gets the same empty state as an exhausted job.
  const isBenchmarkTask =
    (taskDetailResp as any)?.type === "COVERAGE_REVIEW_BENCHMARK";
  const parcel_id: string = (taskDetailResp?.data as any)?.parcel_id ?? "";
  const out_of_stock_predictions: SampleOosPrediction[] =
    (taskDetailResp?.data as any)?.out_of_stock_predictions ?? [];

  const { data: parcelResp, isLoading: parcelIsLoading } = useQuery({
    ...mvpApiGetOosParcelOptions({ path: { parcel_id } }),
    enabled: !!parcel_id,
  });

  // Cache-only read (QueryImageRelated fetches the same key) so the
  // onboarding tutorial can gate its auto-open on the query image actually
  // being on screen, not just the parcel response having arrived — without
  // this, a fresh task's tutorial would open over a blank gray canvas while
  // the image itself is still loading.
  const { data: queryImgResp } = useFetchGalleryImgUrl(
    parcelResp?.query?.image_id ?? "",
  );
  const isTrainingReady =
    !!parcelResp?.query?.image_id && !parcelIsLoading && !!queryImgResp?.objUrl;

  const [isQueryImageLoaded, setIsQueryImageLoaded] = useState(false);
  const [isActiveReferenceLoaded, setIsActiveReferenceLoaded] = useState(false);
  const [isWarmupTimedOut, setIsWarmupTimedOut] = useState(false);
  useEffect(() => {
    setIsQueryImageLoaded(false);
    setIsActiveReferenceLoaded(false);
    setIsWarmupTimedOut(false);
    // Backstop: unlock warm-up regardless after a while, in case neither image
    // signal can ever fire (e.g. no query image_id, so no <img> to load).
    const timer = setTimeout(
      () => setIsWarmupTimedOut(true),
      WARMUP_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [parcel_id]);
  // A stable identity so ReferenceImage's onVisibleLoad effect (which depends
  // on this callback) doesn't re-fire on every unrelated re-render.
  const handleActiveReferenceLoaded = useCallback(
    () => setIsActiveReferenceLoaded(true),
    [],
  );
  // Tasks with no shelf/reference images have nothing to wait for there, so
  // the query image alone counts as "main data loaded" — otherwise warm-up
  // would never fire and the planogram would stay deferred forever.
  const hasReferences = (parcelResp?.references?.length ?? 0) > 0;
  const isWarmupReady =
    isWarmupTimedOut ||
    (isQueryImageLoaded && (isActiveReferenceLoaded || !hasReferences));

  // Per-parcel shape the pilot flow differs on: no shelf images (the right
  // panel drops that tab for the planogram) and grouped query images (a
  // filmstrip of views to annotate). Only on a loaded parcel, so the previous
  // task's shape holds while the next one loads instead of flashing. Declared
  // before the hydration effects so they see this parcel's views.
  useEffect(() => {
    if (!parcelResp) return;
    const store = useCoverageBetaStore.getState();
    store.setHasShelfImages(hasReferences);
    const primaryImageId = parcelResp.query?.image_id ?? "";
    store.setImageGroup(
      primaryImageId,
      getGroupImages(parcelResp, primaryImageId),
    );
  }, [parcelResp, hasReferences]);

  // QA mode always hydrates the boxes from the parent coverage review's full
  // result, stashed on the QA task's `data` by the backend — for both admin
  // browsing and a labeler taking the QA task. `adminResult` (the classic
  // admin-browse source) is instead the QA task's *own* verdict result, which
  // isn't what renders the boxes.
  const qaSourceResult = (taskDetailResp?.data as any)?.source_task_result as
    | Record<string, unknown>
    | undefined;
  const hydrationSource = isQa || isQaEdit ? qaSourceResult : adminResult;

  // Admin (read-only) hydration needs full product details to render the
  // labeler's selections — the result only stores product ids. Both QA flows
  // need the same thing to render the submission under review.
  const adminProductIds = useMemo(() => {
    if (!isAdmin && !isQa && !isQaEdit) return [];
    const sels = (hydrationSource?.product_selections ?? []) as {
      product_ids?: string[];
    }[];
    return [...new Set(sels.flatMap((s) => s?.product_ids ?? []))].filter(
      Boolean,
    );
  }, [isAdmin, isQa, isQaEdit, hydrationSource]);
  const { products: adminProducts, isLoading: adminProductsLoading } =
    useAtlasProducts(adminProductIds);

  // Admin: hydrate the store from the completed result once the parcel and the
  // selected products have loaded, then flip the store read-only.
  useEffect(() => {
    if (!isAdmin || isQa || !adminResult) return;
    if (parcelIsLoading || adminProductsLoading) return;

    const productsById = new Map<string, ProductSchema>();
    adminProducts.forEach((p) => {
      if (p?.id) productsById.set(p.id, p);
    });

    const payload = buildCoverageBetaHydration(
      adminResult,
      parcelResp?.query as Record<string, unknown> | undefined,
      productsById,
    );
    useCoverageBetaStore.getState().hydrateReadOnly(payload);

    return () => {
      useCoverageBetaStore.getState().reset();
    };
  }, [
    isAdmin,
    isQa,
    adminResult,
    parcel_id,
    parcelIsLoading,
    adminProductsLoading,
  ]);

  // Coverage Beta QA: hydrate the boxes read-only from the parent submission
  // (admin-browse and labeler-taking alike — a QA task always reviews an
  // existing submission, never a blank slate), then flip on QA mode and, if
  // browsing an already-completed QA task, preload its verdicts too.
  useEffect(() => {
    if (!isQa || !qaSourceResult) return;
    if (parcelIsLoading || adminProductsLoading) return;

    const productsById = new Map<string, ProductSchema>();
    adminProducts.forEach((p) => {
      if (p?.id) productsById.set(p.id, p);
    });

    const payload = buildCoverageBetaHydration(
      qaSourceResult,
      parcelResp?.query as Record<string, unknown> | undefined,
      productsById,
    );
    const store = useCoverageBetaStore.getState();
    store.hydrateReadOnly(payload);
    store.setQaMode(true);
    const qaItems = getOrderedQaItems(
      payload.idxToFeEmptyArea,
      payload.persons,
    );
    // Default the pager to the first reviewable item (Out of Stock Confirmed
    // → Empty Area → Unblurred Persons → Deleted) so the pulse/highlight has
    // something to point at as soon as QA mode opens, same as
    // `activeEmptyAreaIdx` used to default via `hydrateReadOnly` above.
    store.setActiveQaItemKey(qaItems[0]?.key ?? null);
    if (isAdmin && adminTask?.status === JobTaskStatus.COMPLETED) {
      const qaResult = adminTask.result as any;
      const { verdicts, notes } = mapCoverageBetaQaVerdicts(qaItems, qaResult);
      store.hydrateQaVerdicts(verdicts, notes, {
        unblurredPersonsMissed: !!qaResult?.unblurred_persons_missed,
        boxesShouldHaveBeenDrawn:
          qaResult?.boxes_that_should_have_been_drawn ?? 0,
        qaFeedback: qaResult?.qa_feedback ?? "",
        flagLabeler: !!qaResult?.flag_labeler,
      });
    }

    return () => {
      useCoverageBetaStore.getState().reset();
    };
  }, [
    isQa,
    isAdmin,
    qaSourceResult,
    adminTask?.status,
    adminTask?.result,
    parcel_id,
    parcelIsLoading,
    adminProductsLoading,
  ]);

  // Editable Coverage QA: load the labeler's submission with every tool left
  // on — the reviewer edits it in place and submits the corrected result.
  useEffect(() => {
    if (!isQaEdit || !qaSourceResult) return;
    if (parcelIsLoading || adminProductsLoading) return;

    const productsById = new Map<string, ProductSchema>();
    adminProducts.forEach((p) => {
      if (p?.id) productsById.set(p.id, p);
    });

    const payload = buildCoverageBetaHydration(
      qaSourceResult,
      parcelResp?.query as Record<string, unknown> | undefined,
      productsById,
    );
    useCoverageBetaStore.getState().hydrateEditable(payload);

    return () => {
      useCoverageBetaStore.getState().reset();
    };
    // Keyed on the task id, not `qaSourceResult`: a background refetch of the
    // same task hands back a new object, and re-hydrating would wipe the
    // reviewer's edits.
  }, [
    isQaEdit,
    taskDetailResp?.id,
    !!qaSourceResult,
    parcel_id,
    parcelIsLoading,
    adminProductsLoading,
  ]);

  // Non-admin: seed the store from the parcel's auto detections (a fresh task).
  useEffect(() => {
    if (isAdmin || isQa || isQaEdit) return;
    const { setIdxToFeEmptyArea, setBasePlanogramIds, assignBoxToSlot, reset } =
      useCoverageBetaStore.getState();
    const query = parcelResp?.query as any;
    const emptyAreaDetections: EmptyAreaDetection[] =
      query?.empty_area_detections ?? [];
    const emptyAreaToAisle: number[] = query?.empty_area_to_aisle ?? [];
    setIdxToFeEmptyArea(
      emptyAreaDetections.reduce<IdxToFeEmptyArea>(
        (acc, detection, detectionIdx) => {
          const { id, points, class_index, confidence } = detection;
          acc[detectionIdx] = {
            id,
            points,
            class_index,
            confidence,
            feEmptyAreaIdx: detectionIdx,
            source: "auto",
            isDeleted: false,
            selectedProducts: [],
            isHidden: false,
            isValid: false,
          };
          return acc;
        },
        {},
      ),
    );

    // aisle_to_planogram[aisle] is the planogram for an aisle; keep its
    // positions intact (null aisle → "") so an aisle index stays a valid lookup.
    const aisleToPlanogram = (
      (query?.aisle_to_planogram ?? []) as (string | null)[]
    ).map((p) => p ?? "");
    setBasePlanogramIds(aisleToPlanogram);
    // A box's slot is the aisle index the backend feeds into aisle_to_planogram
    // (empty area → aisle → planogram_id). Use the detection's aisle only when
    // it actually indexes into aisle_to_planogram; otherwise the mappings are
    // inconsistent (e.g. an aisle with no planogram entry) and we fall back to
    // aisle 0 rather than emit an out-of-bounds index.
    emptyAreaDetections.forEach((_, detectionIdx) => {
      const aisle = emptyAreaToAisle[detectionIdx];
      const slot =
        typeof aisle === "number" &&
        aisle >= 0 &&
        aisle < aisleToPlanogram.length
          ? aisle
          : 0;
      assignBoxToSlot(detectionIdx, slot);
    });

    return () => {
      reset();
    };
  }, [parcel_id, parcelIsLoading]);

  // Pre-fill from labels propagated onto this parcel by sibling tasks. Called
  // after the seed effect so a merge never lands before the seed resets the store.
  // Never for a benchmark task: its parcel's labels are the gold answer.
  const relatedFramesEnabled = useRelatedFramesEnabled();
  usePropagatedPrefill({
    jobId: job_id,
    taskId: taskDetailResp?.id ?? "",
    enabled:
      relatedFramesEnabled &&
      !isAdmin &&
      !isQa &&
      !isQaEdit &&
      !isBenchmarkTask,
    isSeeded:
      !isAdmin && !isQa && !isQaEdit && !!parcelResp && !parcelIsLoading,
  });

  // Keep the reference-image count in the store so the prev/next (`←` / `→`)
  // hotkeys can clamp `activeRefImgIdx` without reaching into the parcel.
  useEffect(() => {
    useCoverageBetaStore
      .getState()
      .setReferenceImgCount(parcelResp?.references?.length ?? 0);
  }, [parcelResp]);

  // Marker so a session replay is searchable by task (also a task-loaded signal).
  useEffect(() => {
    if (isAdmin || !taskDetailResp?.id) return;
    posthog?.capture("cb_task_viewed", {
      task_id: taskDetailResp.id,
      job_id,
    });
  }, [isAdmin, taskDetailResp?.id, job_id, posthog]);

  if (!isAdmin && isBenchmarkTask && !benchmarkEnabled) {
    return (
      <div className="flex flex-col justify-center items-center pt-10">
        <div className="mb-4 text-xl">No task found</div>
        <div>
          <Button onClick={() => navigate(`${jobsPath}?tab=${exitTab}`)}>
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }

  // A finished quiz lands here — the exhaustion effect above already exempts
  // benchmark jobs from navigating away. Show the reviewer how they did
  // instead of falling through to render the main editor with no task — this
  // branch always returns something once it's entered.
  if (!isAdmin && isTaskExhausted && isBenchmarkJob) {
    if (benchmarkEnabled && quizMetrics?.status === JobStatus.COMPLETED) {
      return (
        <div className="mx-auto max-w-3xl px-4 pt-10">
          <QuizMetricCard
            title="Quiz complete"
            status={quizMetrics.status as JobStatus}
            subtitle={`${quizMetrics.store_name} · ${new Date(
              quizMetrics.created_at,
            ).toLocaleString()}`}
            precision={quizMetrics.precision}
            correct={quizMetrics.correct}
            answered={quizMetrics.answered}
            avgSecondsPerTask={quizMetrics.avg_seconds_per_task}
            tasksPerHour={quizMetrics.tasks_per_hour}
            onBack={() => navigate(`${jobsPath}?tab=my-quizzes`)}
          />
        </div>
      );
    }
    if (benchmarkEnabled && quizMetricsLoading) {
      return (
        <div className="flex justify-center items-center h-40 relative">
          <LoadingOverlay visible />
        </div>
      );
    }
    // Gate off, or the gate is on but metrics haven't resolved to COMPLETED
    // (quiz not actually finished yet, or the fetch failed) — generic empty
    // state rather than a card claiming results that aren't there.
    return (
      <div className="flex flex-col justify-center items-center pt-10">
        <div className="mb-4 text-xl">No task found</div>
        <div>
          <Button onClick={() => navigate(`${jobsPath}?tab=${exitTab}`)}>
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="flex h-full"
        // style={{ marginTop: 0, height: `calc(100vh - ${HEADER_HEIGHT}px)` }}
        // style={{ marginTop: 0, height: `calc(100vh - ${HEADER_HEIGHT}px)` }}
        style={{
          marginTop: adminTask ? HEADER_HEIGHT : 0,
          height: adminTask
            ? `calc(100vh - ${2 * HEADER_HEIGHT}px)`
            : `calc(100vh - ${HEADER_HEIGHT}px)`,
        }}
        // style={{
        //   marginTop: adminTask ? HEADER_HEIGHT : 0,
        //   height: `calc(100vh - ${2 * HEADER_HEIGHT}px)`,
        // }}
        // style={{
        //   marginTop: adminTask ? 0 : -HEADER_HEIGHT,
        //   height: adminTask ? `calc(100vh - ${HEADER_HEIGHT}px)` : "100vh",
        // }}
      >
        {/* <div
        style={{ width: `${COVERAGE_BETA_SIDEBAR_WIDTH}px` }}
        // className="border border-blue-500 h-full"
        className="h-full"
      ></div> */}
        <CoverageBetaHeader
          taskDetailResp={taskDetailResp}
          qaMode={isQa}
          qaEdit={isQaEdit}
        />

        <div
          style={{ minWidth: COVERAGE_BETA_SIDEBAR_WIDTH }}
          data-cb-tour="sidebar"
        >
          <CoverageBetaSummaryView
            out_of_stock_predictions={out_of_stock_predictions}
          />
        </div>

        <div
          // className="border border-blue-500 flex h-full"
          className="flex h-full"
          style={{ width: `calc(100% - ${COVERAGE_BETA_SIDEBAR_WIDTH}px)` }}
          // style={{ width: `calc(100%)` }}
        >
          <div
            // className="border border-blue-500 h-full"
            className="h-full"
            style={{ width: `${QUERY_WIDTH_PERCENT}%` }}
            data-cb-tour="query-pane"
          >
            <QueryImageRelated
              jobId={job_id}
              taskId={taskDetailResp?.id ?? ""}
              out_of_stock_predictions={out_of_stock_predictions}
              parcelResp={parcelResp}
              isBenchmarkTask={isBenchmarkTask}
              onImageLoaded={() => setIsQueryImageLoaded(true)}
              isAdmin={isAdmin}
            />
          </div>

          <div
            // className="border border-blue-500 h-full"
            className="h-full"
            style={{ width: `${RIGHT_WIDTH_PERCENT}%` }}
            data-cb-tour="right-panel"
          >
            <CoverageBetaRightPanel
              out_of_stock_predictions={out_of_stock_predictions}
              parcelResp={parcelResp}
              onActiveReferenceLoaded={handleActiveReferenceLoaded}
              isWarmupReady={isWarmupReady}
            />
          </div>
        </div>
      </div>

      {/* Idle tracking — viewer mode only (mirrors CoverageReview). Sends the
          /v1/tasks/{task_id}/activity heartbeat and shows the idle/exit modals. */}
      {!adminTask && (
        <IdleTimeProvider
          taskId={taskDetailResp?.id || ""}
          timeout={
            isDev() || tutorialActive ? 1_000_000_000 : undefined // don't show warning modal in dev env, or while the tutorial is open
          }
        >
          <IdleWarningModal jobId={job_id} taskId={taskDetailResp?.id || ""} />
          <ExitModal />
          {/* Records active/idle timing for the task's result.metadata. */}
          <CoverageBetaActivityTracker taskId={taskDetailResp?.id || ""} />
        </IdleTimeProvider>
      )}

      {/* Onboarding tutorial — labeler view only. Never admin (no header,
          nothing to spotlight in the read-only view) or QA (a materially
          different footer/store shape the labeler steps don't describe). */}
      {!isAdmin && !isQa && !isQaEdit && (
        <CoverageBetaTraining
          ready={isTrainingReady}
          taskId={taskDetailResp?.id ?? ""}
          autoOpenDisabled={isBenchmarkTask}
        />
      )}
    </>
  );
};
